import React, { useState } from "react";
import {
  copyColumnToClipboard,
  copyTableToClipboard,
  showCopyNotification,
} from "../utils/clipboardUtils";

/**
 * Professional Column Actions component for copying table data
 * Provides elegant UI/UX for column-specific operations
 */
const ColumnActions = ({ data, columns, tableName, onNotification }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCopying, setIsCopying] = useState(false);

  // Find the Room Allocation column specifically
  const roomAllocationColumn = columns.find((col) => {
    // Check if it's the Room Allocation column by ID or header content
    if (col.id === "room_allocation") {
      return true;
    }

    if (typeof col.Header === "function") {
      // For functional headers, check if it renders "Room Allocation"
      try {
        const headerElement = col.Header();
        return headerElement?.props?.children?.some?.(
          (child) => typeof child === "string" && child.includes("Room Allocation")
        );
      } catch (e) {
        return false;
      }
    }
    return (
      col.Header === "Room Allocation" ||
      col.accessor === "room_allocation" ||
      (typeof col.accessor === "function" && col.Header === "Room Allocation")
    );
  });

  // Quick copy function for Room Allocation column
  const handleQuickCopyRoomAllocation = async () => {
    if (!roomAllocationColumn) {
      onNotification?.("Room Allocation column not found", "error");
      return;
    }

    setIsCopying(true);
    try {
      const result = await copyColumnToClipboard(
        data,
        "room_allocation", // Use the key directly
        roomAllocationColumn.headerLabel || "Room Allocation",
        true
      );

      showCopyNotification(result);
      onNotification?.(result.message, result.success ? "success" : "error");
    } catch (error) {
      const errorMessage = `Failed to copy Room Allocation: ${error.message}`;
      console.error(errorMessage);
      onNotification?.(errorMessage, "error");
    } finally {
      setIsCopying(false);
    }
  };

  // Copy any column function
  const handleCopyColumn = async (column) => {
    setIsCopying(true);
    setIsDropdownOpen(false);

    try {
      let columnKey;
      let columnHeader;

      // Handle different column types - prioritize ID, then accessor, then extract from header
      if (column.id) {
        columnKey = column.id;
      } else if (typeof column.accessor === "string") {
        columnKey = column.accessor;
      } else {
        // Fallback for function accessors
        columnKey = "room_allocation"; // Default for function accessors
      }

      // Handle different header types with special case for Room Allocation
      if (column.id === "room_allocation" || column.accessor === "room_allocation") {
        columnHeader = column.headerLabel || "Room Allocation";
      } else if (typeof column.Header === "function") {
        // For functional headers, extract the text more comprehensively
        try {
          const headerElement = column.Header();
          if (headerElement?.props?.children) {
            const textContent = headerElement.props.children
              .filter((child) => typeof child === "string")
              .join(" ")
              .trim();
            columnHeader = textContent || "Column";
          } else {
            columnHeader = "Column";
          }
        } catch (e) {
          columnHeader = "Column";
        }
      } else {
        columnHeader = column.Header || "Column";
      }

      const result = await copyColumnToClipboard(data, columnKey, columnHeader, true);

      showCopyNotification(result);
      onNotification?.(result.message, result.success ? "success" : "error");
    } catch (error) {
      const errorMessage = `Failed to copy column: ${error.message}`;
      console.error(errorMessage);
      onNotification?.(errorMessage, "error");
    } finally {
      setIsCopying(false);
    }
  };

  // Copy entire table function
  const handleCopyTable = async () => {
    setIsCopying(true);
    setIsDropdownOpen(false);

    try {
      // Prepare columns with proper headers for table copy
      const processedColumns = columns.map((col) => {
        let header;
        if (typeof col.Header === "function") {
          try {
            const headerElement = col.Header();
            header =
              headerElement?.props?.children?.find?.((child) => typeof child === "string") ||
              col.id ||
              col.accessor ||
              "Column";
          } catch (e) {
            header = col.id || col.accessor || "Column";
          }
        } else {
          header = col.Header || col.id || col.accessor || "Column";
        }

        return {
          ...col,
          Header: header,
        };
      });

      const result = await copyTableToClipboard(data, processedColumns, tableName);
      showCopyNotification(result);
      onNotification?.(result.message, result.success ? "success" : "error");
    } catch (error) {
      const errorMessage = `Failed to copy table: ${error.message}`;
      console.error(errorMessage);
      onNotification?.(errorMessage, "error");
    } finally {
      setIsCopying(false);
    }
  };

  if (!data || data.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-3 mb-4">
      {/* Quick Copy Room Allocation Button - Primary Action */}
      {roomAllocationColumn && (
        <button
          onClick={handleQuickCopyRoomAllocation}
          disabled={isCopying}
          className={`
            inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg
            transition-all duration-200 transform hover:scale-105
            ${
              isCopying
                ? "bg-gray-400 text-white cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg"
            }
          `}
          title="Copy Room Allocation column to clipboard (Excel-compatible)"
        >
          {isCopying ? (
            <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          ) : (
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 5H6a2 2 0 00-2 2v6a2 2 0 002 2h2m2-6a2 2 0 012-2h2a2 2 0 012 2v6a2 2 0 01-2 2h-2m-6 0h4"
              />
            </svg>
          )}
          Copy Room Allocation
        </button>
      )}

      {/* More Options Dropdown */}
      <div className="relative">
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          disabled={isCopying}
          className={`
            inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg border
            transition-all duration-200
            ${
              isCopying
                ? "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400 shadow-sm"
            }
          `}
          title="More copy options"
        >
          <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
            />
          </svg>
          More
          <svg className="h-4 w-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Dropdown Menu */}
        {isDropdownOpen && (
          <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
            <div className="py-2">
              <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100">
                Copy Individual Columns
              </div>

              {/* Individual Column Copy Options */}
              <div className="max-h-40 overflow-y-auto">
                {columns.map((column, index) => {
                  // Extract column header for display
                  let columnHeader;

                  // Special handling for Room Allocation column
                  if (column.id === "room_allocation" || column.accessor === "room_allocation") {
                    columnHeader = column.headerLabel || "Room Allocation";
                  } else if (typeof column.Header === "function") {
                    try {
                      const headerElement = column.Header();
                      // Look for text content more comprehensively
                      if (headerElement?.props?.children) {
                        const textContent = headerElement.props.children
                          .filter((child) => typeof child === "string")
                          .join(" ")
                          .trim();
                        columnHeader =
                          textContent || column.id || column.accessor || `Column ${index + 1}`;
                      } else {
                        columnHeader = column.id || column.accessor || `Column ${index + 1}`;
                      }
                    } catch (e) {
                      columnHeader = column.id || column.accessor || `Column ${index + 1}`;
                    }
                  } else {
                    columnHeader =
                      column.Header || column.id || column.accessor || `Column ${index + 1}`;
                  }

                  return (
                    <button
                      key={index}
                      onClick={() => handleCopyColumn(column)}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors duration-150"
                      title={`Copy ${columnHeader} column to clipboard`}
                    >
                      <div className="flex items-center">
                        <svg
                          className="h-4 w-4 mr-2 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                          />
                        </svg>
                        {columnHeader}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="border-t border-gray-100 mt-2">
                <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Copy Entire Table
                </div>
                <button
                  onClick={handleCopyTable}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors duration-150"
                  title="Copy entire table with headers to clipboard"
                >
                  <div className="flex items-center">
                    <svg
                      className="h-4 w-4 mr-2 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h2a2 2 0 002-2"
                      />
                    </svg>
                    Copy All Data ({data.filter((row) => !row.is_section_header).length} rows)
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Instructions Text */}
      <div className="text-xs text-gray-500 ml-2">
        📋 Copied data is Excel-compatible - paste directly into spreadsheets
      </div>

      {/* Click outside to close dropdown */}
      {isDropdownOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setIsDropdownOpen(false)} />
      )}
    </div>
  );
};

export default ColumnActions;
